import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { create } from 'zustand';
import { CHESS_API, type Chess } from '@chess/api-chess';
import { REVIEW_API, type Review } from '@chess/api-review';
import { useSelect } from '@chess/api-review/react';
import { PUZZLES_API, type Puzzles } from '@chess/api-puzzles';
import { rowsOf } from './rows.ts';
import { MomentsTable } from './MomentsTable.tsx';
import './moments.css';
import { type Phase } from '@chess/api-model';

// The reader's blunders and misses in a stage, as a table any stage panel
// shows: this plugin contributes it to each stage's sections slot. Rows come
// from the review session (its scan, stages and reader side); the blunder or
// miss label is the session scan's own rule (`GameScan.kind`), never a copy.
// A row starts that move's puzzle through the `puzzles` API, which
// `review-puzzles` provides; without it the rows are not targets.

const PHASES: Phase[] = ['opening', 'middlegame', 'ending'];
const SLOT: Record<Phase, string> = {
  opening: 'review.opening.sections',
  middlegame: 'review.middlegame.sections',
  ending: 'review.ending.sections',
};

/** What a stage tab hands its sections. */
interface SectionParams {
  gameId?: string;
  phase: Phase;
  fromPly: number;
  toPly: number;
}

const useApis = create<{ review?: Review; chess?: Chess; puzzles?: Puzzles; tick: number }>(() => ({ tick: 0 }));

const Section: React.FC<{ params: SectionParams; Extension: PiletApi['Extension'] }> = ({ params, Extension }) => {
  const { review, chess, puzzles } = useApis();
  useApis((s) => s.tick); // re-render when a puzzle's record changes
  const view = useSelect(review, (s) => ({ game: s.game, scan: s.scan, reader: s.reader }));
  // The board's position (the ply looked back at while in a puzzle; none on a line of the reader's own): its row, if a mistake, is marked.
  const currentPly = useSelect(review, (s) => (s.exploring ? undefined : (s.browse ?? s.ply)));
  const rows = rowsOf(view, params.fromPly, params.toPly, chess, puzzles ? (ply) => puzzles.statusOf(ply) : undefined);
  if (rows.length === 0) return null;
  // The shared section frame draws the title and spacing and hides itself when empty.
  return (
    <Extension name="review.stageSection" params={{ title: 'Blunders and misses' }}>
      <MomentsTable rows={rows} currentPly={currentPly} onGo={(ply) => review?.go(ply)} onPuzzle={puzzles ? (row) => puzzles.startForMove(row.ply) : undefined} />
    </Extension>
  );
};

export function setup(app: PiletApi) {
  void Promise.all([app.getApi<Review>(REVIEW_API), app.getApi<Chess>(CHESS_API)]).then(([review, chess]) => useApis.setState({ review, chess }));
  // The puzzles plugin is optional: without it the table still lists the moves.
  void app.getApi<Puzzles>(PUZZLES_API).then((puzzles) => {
    useApis.setState({ puzzles });
    puzzles.subscribe(() => useApis.setState((s) => ({ tick: s.tick + 1 })));
  });
  for (const phase of PHASES) app.registerExtension(SLOT[phase], ({ params }: { params: SectionParams }) => <Section params={params} Extension={app.Extension} />, { order: 10 });
}
